import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RestoreIcon from '@mui/icons-material/Restore';
import ChipInput from 'datascient-ui-library/src/lib/Molecules/MultiInputField/MultiInput';
import { dsVar, ink } from '../theme/tokens';

/**
 * Renders an activity template's configuration.task_configuration list inside the
 * node edit drawer. Shared by AgentFlowEditor and TaskFlowEditor so the two
 * drawers cannot drift apart — the only difference between them is `accent`.
 *
 * Each element is a collapsible accordion card showing a preview of its current
 * value. Elements, and MULTICOLUMN rows within them, are soft-deleted with an
 * `active: false` flag rather than being spliced out, so nothing is lost from
 * the payload and everything stays restorable.
 *
 * Fully controlled: the parent owns the config array because handleSaveEditedNode
 * PUTs it verbatim.
 */

/* ── Normalisation ──────────────────────────────────────────────────────────
 * The API sends snake_case (column_headers / is_editable / allow_display) while
 * earlier code here read camelCase. These read either shape so both work.
 */
export const getColumnHeaders = (f) => f?.column_headers ?? f?.columnHeaders ?? [];
export const isColumnEditable = (c) => (c?.is_editable ?? c?.isEditable ?? true) !== false;
export const isColumnVisible = (c) =>
  c?.key !== 'active' && (c?.allow_display ?? c?.allowDisplay ?? true) !== false;
export const isFieldRequired = (f) => (f?.is_required ?? f?.isRequired) === true;
// Absent `active` means active. Applies identically to elements and to rows.
export const isActive = (x) => x?.active !== false;
// Replaces `row[col.key] || ''`, which blanked out 0, 0.005 and false. Note this
// means an edited numeric cell writes back a string — consistent with the API's
// own mixed convention ("min_segments": "3" alongside 0.005), so no coercion is
// applied without a backend contract.
export const cellToString = (v) => (v === null || v === undefined ? '' : String(v));

const TEXT_TYPES = ['TEXTINPUT', 'TEXTAREA', 'TEXTAREA_L', 'TEXTAREA_XL'];
const TEXT_ROWS = { TEXTINPUT: 1, TEXTAREA: 3, TEXTAREA_L: 5, TEXTAREA_XL: 12 };

const truncate = (s, n = 60) => (s.length > n ? `${s.slice(0, n - 1)}…` : s);
const oneLine = (v) => cellToString(v).replace(/\s+/g, ' ').trim();
const chipLabel = (v) => (typeof v === 'object' && v !== null ? v.name : cellToString(v));
const plural = (noun, n) => (n === 1 ? noun : `${noun}s`);
const titleCaseFromKey = (k) =>
  String(k)
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());

const inputSx = {
  '& .MuiInputBase-root': { color: dsVar('fg-body'), fontSize: '14px', backgroundColor: dsVar('surface') },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: dsVar('line') },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#159DFF' },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#159DFF' },
};

const emptyTextSx = { color: dsVar('fg-muted'), fontSize: '12px', fontStyle: 'italic' };

/** Collapsible "N removed …" affordance, used at both the form and row level. */
const RemovedStrip = ({ count, noun, open, onToggle, sx, children }) => (
  <>
    <Box
      onClick={onToggle}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        cursor: 'pointer',
        userSelect: 'none',
        color: dsVar('fg-muted'),
        fontSize: '11px',
        fontWeight: 600,
        ...sx,
      }}
    >
      {`${count} removed ${plural(noun, count)}`}
      <ExpandMoreIcon
        sx={{
          fontSize: 14,
          transform: open ? 'rotate(180deg)' : 'none',
          transition: 'transform 0.2s',
        }}
      />
    </Box>
    <Collapse in={open}>{children}</Collapse>
  </>
);

/** One struck-through entry inside a RemovedStrip, with a restore button. */
const RemovedItem = ({ accent, title, onRestore, children }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      py: 0.75,
      px: 1,
      mt: 0.5,
      border: `1px dashed ${dsVar('line')}`,
      borderRadius: '6px',
    }}
  >
    <Typography
      noWrap
      sx={{ flex: 1, minWidth: 0, fontSize: '12px', color: dsVar('fg-muted'), textDecoration: 'line-through' }}
    >
      {children}
    </Typography>
    <IconButton
      size="small"
      title={title}
      onClick={onRestore}
      sx={{ color: dsVar('fg-muted'), '&:hover': { color: accent } }}
    >
      <RestoreIcon sx={{ fontSize: 16 }} />
    </IconButton>
  </Box>
);

/**
 * Columns for a MULTICOLUMN field. Falls back to inferring them from the row
 * data so a payload without column_headers renders something rather than the
 * empty bordered box this component replaces.
 */
const resolveColumns = (field, rows) => {
  const headers = getColumnHeaders(field);
  const derived = headers.length
    ? headers
    : [...new Set(rows.flatMap((r) => Object.keys(r || {})))]
        .filter((k) => k !== 'active')
        .map((k) => ({ key: k, label: titleCaseFromKey(k), is_editable: true }));
  return { cols: derived.filter(isColumnVisible), inferred: headers.length === 0 };
};

/** Summary line shown under each accordion label. */
const buildPreview = (field, readOnly) => {
  const { type, value } = field || {};

  if (TEXT_TYPES.includes(type)) {
    const text = oneLine(value);
    return text ? { text: truncate(text), muted: false } : { text: 'Not set', muted: true };
  }

  if (type === 'MULTITEXT') {
    const items = Array.isArray(value) ? value : [];
    return items.length
      ? { text: truncate(items.map(chipLabel).join(', ')), muted: false }
      : { text: 'None set', muted: true };
  }

  if (type === 'MULTICOLUMN') {
    const rows = Array.isArray(value) ? value : [];
    const { cols } = resolveColumns(field, rows);
    const noun = cols[0]?.label ? String(cols[0].label).toLowerCase() : 'row';
    const active = rows.filter(isActive).length;
    const removed = rows.length - active;
    if (!rows.length) return { text: `No ${plural(noun, 0)}`, muted: true };
    const suffix = !readOnly && removed > 0 ? ` · ${removed} removed` : '';
    return { text: `${active} ${plural(noun, active)}${suffix}`, muted: false };
  }

  if (typeof value === 'object' && value !== null) return { text: 'Structured value', muted: false };
  const text = oneLine(value);
  return text ? { text: truncate(text), muted: false } : { text: 'Not set', muted: true };
};

const TaskConfigurationForm = ({
  config,
  readOnly = false,
  accent = '#FFDE59',
  onFieldChange,
  onCellChange,
  onFieldActiveChange,
  onRowActiveChange,
}) => {
  const [expanded, setExpanded] = useState({});
  const [showRemovedRows, setShowRemovedRows] = useState({});
  const [showRemovedFields, setShowRemovedFields] = useState(false);

  const fields = Array.isArray(config) ? config : [];
  const activeFields = fields.filter(isActive);
  const removedFields = fields.filter((f) => !isActive(f));

  const toggleField = (fieldId) => (_, open) =>
    setExpanded((prev) => ({ ...prev, [fieldId]: open }));

  const renderMultiColumn = (field, fieldId) => {
    const rows = Array.isArray(field.value) ? field.value : [];
    const { cols, inferred } = resolveColumns(field, rows);
    // Row identity is the index into the *unfiltered* array — soft-deleted rows
    // stay in place, so a filtered index would edit the wrong row.
    const indexed = rows.map((row, i) => ({ row, i }));
    const activeRows = indexed.filter(({ row }) => isActive(row));
    const removedRows = indexed.filter(({ row }) => !isActive(row));
    const noun = cols[0]?.label ? String(cols[0].label).toLowerCase() : 'row';
    const showRowActions = !readOnly;

    if (!rows.length) {
      return (
        <TableContainer
          component={Paper}
          sx={{ backgroundColor: dsVar('page'), border: `1px solid ${dsVar('line')}`, borderRadius: '8px' }}
        >
          <Typography sx={{ ...emptyTextSx, p: 2 }}>
            {cols.length ? 'No rows configured' : 'No columns configured for this field'}
          </Typography>
        </TableContainer>
      );
    }

    return (
      <>
        <TableContainer
          component={Paper}
          sx={{ backgroundColor: dsVar('page'), border: `1px solid ${dsVar('line')}`, borderRadius: '8px' }}
        >
          <Table size="small">
            <TableHead>
              <TableRow>
                {cols.map((col) => (
                  <TableCell
                    key={col.key}
                    sx={{
                      color: dsVar('fg-2'),
                      borderBottom: `1px solid ${dsVar('line')}`,
                      fontWeight: 600,
                      fontSize: '12px',
                    }}
                  >
                    {col.label}
                  </TableCell>
                ))}
                {showRowActions && (
                  <TableCell sx={{ borderBottom: `1px solid ${dsVar('line')}`, width: 44, padding: '4px' }} />
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {!activeRows.length && (
                <TableRow>
                  <TableCell
                    colSpan={cols.length + (showRowActions ? 1 : 0)}
                    sx={{ borderBottom: 'none', ...emptyTextSx }}
                  >
                    All rows removed
                  </TableCell>
                </TableRow>
              )}
              {activeRows.map(({ row, i }) => (
                <TableRow key={`row-${i}`}>
                  {cols.map((col) => {
                    const editable = isColumnEditable(col);
                    return (
                      <TableCell
                        key={col.key}
                        sx={{ borderBottom: `1px solid ${dsVar('line')}`, padding: '8px' }}
                      >
                        <TextField
                          size="small"
                          fullWidth
                          variant="outlined"
                          value={cellToString(row[col.key])}
                          disabled={readOnly || !editable}
                          onChange={(e) => onCellChange(field.key, i, col.key, e.target.value)}
                          sx={{
                            '& .MuiInputBase-root': {
                              color: dsVar('fg-body'),
                              fontSize: '13px',
                              backgroundColor: editable ? dsVar('surface') : 'transparent',
                            },
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: editable ? dsVar('line') : 'transparent',
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                              borderColor: editable ? '#159DFF' : 'transparent',
                            },
                            '&.Mui-disabled': { opacity: 0.7 },
                          }}
                        />
                      </TableCell>
                    );
                  })}
                  {showRowActions && (
                    <TableCell
                      sx={{ borderBottom: `1px solid ${dsVar('line')}`, width: 44, padding: '4px' }}
                    >
                      <IconButton
                        size="small"
                        title="Remove row"
                        onClick={() => onRowActiveChange(field.key, i, false)}
                        sx={{ color: dsVar('fg-muted'), '&:hover': { color: ink('#FF4D4D') } }}
                      >
                        <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {inferred && (
          <Typography sx={{ color: dsVar('fg-muted'), fontSize: '11px', mt: 0.75 }}>
            Columns inferred from data
          </Typography>
        )}

        {showRowActions && removedRows.length > 0 && (
          <RemovedStrip
            count={removedRows.length}
            noun={noun}
            open={!!showRemovedRows[fieldId]}
            onToggle={() => setShowRemovedRows((prev) => ({ ...prev, [fieldId]: !prev[fieldId] }))}
            sx={{ mt: 1 }}
          >
            {removedRows.map(({ row, i }) => (
              <RemovedItem
                key={`removed-${i}`}
                accent={accent}
                title={`Restore ${noun}`}
                onRestore={() => onRowActiveChange(field.key, i, true)}
              >
                {cols.map((col) => cellToString(row[col.key])).filter(Boolean).join(' · ')}
              </RemovedItem>
            ))}
          </RemovedStrip>
        )}
      </>
    );
  };

  const renderBody = (field, fieldId) => {
    const { type, label, info, key, value } = field;

    if (TEXT_TYPES.includes(type)) {
      return (
        <TextField
          size="small"
          fullWidth
          multiline={type !== 'TEXTINPUT'}
          rows={TEXT_ROWS[type]}
          variant="outlined"
          disabled={readOnly}
          value={cellToString(value)}
          onChange={(e) => onFieldChange(key, e.target.value)}
          sx={inputSx}
        />
      );
    }

    if (type === 'MULTITEXT') {
      const items = Array.isArray(value) ? value : [];
      // ChipInput exposes no `disabled` prop, so read-only mode renders static
      // chips instead of the editor.
      if (readOnly) {
        return items.length ? (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
            {items.map((item, i) => (
              <Chip
                key={i}
                size="small"
                label={chipLabel(item)}
                sx={{ backgroundColor: dsVar('raised'), color: dsVar('fg-body'), border: `1px solid ${dsVar('line')}` }}
              />
            ))}
          </Box>
        ) : (
          <Typography sx={emptyTextSx}>None set</Typography>
        );
      }
      return (
        <Box
          sx={{
            '& .MuiInputBase-root': {
              color: dsVar('fg-body'),
              backgroundColor: dsVar('surface'),
              borderRadius: '8px',
              fontSize: '14px',
            },
            '& .MuiOutlinedInput-notchedOutline': { borderColor: dsVar('line') },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#159DFF' },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#159DFF' },
            '& .MuiChip-root': {
              backgroundColor: dsVar('raised'),
              color: dsVar('fg-body'),
              border: `1px solid ${dsVar('line')}`,
            },
            '& .MuiChip-deleteIcon': { color: dsVar('fg-2'), '&:hover': { color: '#FF4D4D' } },
            '& .MuiTypography-root': { display: 'none' }, // Hides the internal question label from ChipInput to use our own
          }}
        >
          <ChipInput
            question=""
            minRows={3}
            placeholder={info || label}
            values={items}
            onChipChange={(chips) => onFieldChange(key, chips)}
          />
        </Box>
      );
    }

    if (type === 'MULTICOLUMN') return renderMultiColumn(field, fieldId);

    // Unknown type. These already survive a save (task_configuration is PUT
    // verbatim), they were just invisible before — which reads as data loss.
    return (
      <>
        <Typography sx={{ color: dsVar('fg-2'), fontSize: '11px', mb: 1 }}>
          {`Unsupported field type: ${String(type || 'unknown')} — this value is preserved when you save.`}
        </Typography>
        <TextField
          size="small"
          fullWidth
          multiline
          maxRows={8}
          disabled
          variant="outlined"
          value={
            typeof value === 'object' && value !== null
              ? JSON.stringify(value, null, 2)
              : cellToString(value)
          }
          sx={{
            ...inputSx,
            '& .MuiInputBase-root': {
              color: dsVar('fg-body'),
              fontSize: '12px',
              backgroundColor: dsVar('surface'),
              fontFamily: "'JetBrains Mono', monospace",
            },
          }}
        />
      </>
    );
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      {!activeFields.length && (
        <Typography sx={{ ...emptyTextSx, py: 2 }}>No parameters configured</Typography>
      )}

      {activeFields.map((field, idx) => {
        const fieldId = field.key || `idx-${idx}`;
        const { text: previewText, muted } = buildPreview(field, readOnly);
        // Removal matches on field.key, so a keyless element cannot be restored.
        const canRemove = !readOnly && !!field.key;

        return (
          <Accordion
            key={fieldId}
            disableGutters
            expanded={!!expanded[fieldId]}
            onChange={toggleField(fieldId)}
            sx={{
              backgroundColor: dsVar('surface'),
              color: dsVar('fg-body'),
              border: `1px solid ${dsVar('line')}`,
              borderRadius: '8px !important',
              boxShadow: 'none',
              overflow: 'hidden',
              mb: 1,
              '&:before': { display: 'none' },
              '&.Mui-expanded': { borderColor: `${accent}55` },
              '&:hover': { borderColor: `${accent}55` },
              '&:hover .tcf-remove': { opacity: 1 },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon sx={{ color: dsVar('fg-2') }} />}
              sx={{
                minHeight: '56px !important',
                gap: 1,
                '& .MuiAccordionSummary-content': {
                  my: '10px',
                  minWidth: 0,
                  alignItems: 'center',
                  gap: 1,
                },
              }}
            >
              <Box sx={{ minWidth: 0, flex: 1, display: 'flex', flexDirection: 'column', gap: '2px' }}>
                <Typography sx={{ fontSize: '13px', fontWeight: 600, color: dsVar('fg-body') }}>
                  {field.label}
                  {isFieldRequired(field) && (
                    <Box component="span" sx={{ color: ink('#FF4D4D'), ml: 0.5 }}>
                      *
                    </Box>
                  )}
                </Typography>
                <Typography
                  noWrap
                  title={previewText}
                  sx={{
                    width: '100%',
                    fontSize: '11px',
                    color: muted ? dsVar('fg-muted') : dsVar('fg-2'),
                    fontStyle: muted ? 'italic' : 'normal',
                  }}
                >
                  {previewText}
                </Typography>
              </Box>
              {canRemove && (
                <IconButton
                  className="tcf-remove"
                  size="small"
                  title="Remove parameter"
                  // Without stopPropagation the summary swallows the click and
                  // toggles the accordion as well.
                  onClick={(e) => {
                    e.stopPropagation();
                    onFieldActiveChange(field.key, false);
                  }}
                  onFocus={(e) => e.stopPropagation()}
                  sx={{
                    opacity: 0,
                    transition: 'opacity 0.15s',
                    color: dsVar('fg-muted'),
                    '&:hover': { color: ink('#FF4D4D') },
                    '&:focus-visible': { opacity: 1 },
                  }}
                >
                  <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                </IconButton>
              )}
            </AccordionSummary>
            <AccordionDetails sx={{ borderTop: `1px solid ${dsVar('line')}`, pt: 2, px: 2, pb: 2 }}>
              {renderBody(field, fieldId)}
            </AccordionDetails>
          </Accordion>
        );
      })}

      {!readOnly && removedFields.length > 0 && (
        <RemovedStrip
          count={removedFields.length}
          noun="parameter"
          open={showRemovedFields}
          onToggle={() => setShowRemovedFields((v) => !v)}
          sx={{ mt: 1.5, pt: 1.5, borderTop: `1px solid ${dsVar('line')}` }}
        >
          {removedFields.map((field, idx) => (
            <RemovedItem
              key={field.key || `removed-idx-${idx}`}
              accent={accent}
              title="Restore parameter"
              onRestore={() => onFieldActiveChange(field.key, true)}
            >
              {field.label}
              {isFieldRequired(field) && (
                <Box component="span" sx={{ color: ink('#FF4D4D'), ml: 0.5 }}>
                  *
                </Box>
              )}
            </RemovedItem>
          ))}
        </RemovedStrip>
      )}
    </Box>
  );
};

export default TaskConfigurationForm;
